import gamTimeouts from '../../consts/config/gamTimeouts';
import uamTimeouts from '../../consts/config/uamTimeouts';
import * as logMessageTypes from '../../consts/log/logMessageTypes';
import * as dataCollectionTypes from '../../consts/uam/dataCollectionTypes';
import * as yapActionStatusTypes from '../../consts/yap/yapActionStatusTypes';
import * as yapFlowTypes from '../../consts/yap/yapFlowTypes';
import * as yapRunCommandActionTypes from '../../consts/yap/yapRunCommandActionTypes';
import {
    setYapAutoCleaningInProgress,
    setYapLastRequestId,
    setYapRequestActionTypeData,
    setYapUpdateSettingsInProgress,
} from '../../state/ducks/yapEncrypted';
import {makeSelectAssetIdByType, makeSelectIsYapUpdateSettingsInProgress} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import helpers from '../../utils/helpers';
import appErrorService from '../app/appErrorService';
import backendService from '../app/backendService';
import cmService from '../communicationLayer/cmService';
import GamClient from '../gam/gamClient';
import log from '../logger/log';
import {getCurrentDataCollectionType} from '../uam/dataCollectionService';
import UamClient from '../uam/uamClient';
import yapConnectionService from '../yap/yapConnectionService';
import yapFlowTimeoutService from '../yap/yapFlowTimeoutService';

const waitUntilDataCollectionCanceled = async ({
    timer = uamTimeouts.UAM_DATA_COLLECTION_WAIT_FOR_CANCEL_TIMEOUT,
    checkOnlyGlobal,
} = {}) => {
    const isGamBackend = backendService.isGamBackend();
    const dataCollectionType = getCurrentDataCollectionType();

    if (!dataCollectionType) return true;

    if (checkOnlyGlobal && dataCollectionType !== dataCollectionTypes.DATA_COLLECTION_GLOBAL) {
        return true;
    }

    if (--timer < 0) {
        log.error(
            'yapClientService: Yap Data Collection failed to cancel.',
            isGamBackend ? logMessageTypes.GAM : logMessageTypes.UAM
        );
        return false;
    }

    await helpers.timeout(1000);
    return await waitUntilDataCollectionCanceled({timer, checkOnlyGlobal});
};

const updateSettingsFailedAction = (isHolder, actionType) => {
    dispatch(setYapRequestActionTypeData({isHolder, actionType, newValue: {status: yapActionStatusTypes.FAILED}}));
    dispatch(setYapUpdateSettingsInProgress(false, isHolder));

    if (actionType === yapRunCommandActionTypes.FIND_DEVICE) {
        log.info(
            `yapClientService: End ${yapRunCommandActionTypes.FIND_DEVICE} process. Status: ${yapActionStatusTypes.FAILED}. isHolder: ${isHolder}`
        );
    }

    if (actionType === yapRunCommandActionTypes.CLEAN_DEVICE) {
        dispatch(setYapAutoCleaningInProgress(false));
    } else if (actionType !== yapRunCommandActionTypes.RESET_DEVICE) {
        appErrorService.showDeviceSettingsGlobalError();
    }
    return false;
};

const runYapCommand = async ({isHolder, methodName, actionType, params, customTimeout, isNeedCancel}) => {
    const isGamBackend = backendService.isGamBackend();
    const state = getState();
    const isYapUpdateSettingsInProgress = makeSelectIsYapUpdateSettingsInProgress(isHolder)(state);

    if (isYapUpdateSettingsInProgress) return false;

    log.info(`Start ${actionType} process. isHolder: ${isHolder}`);
    yapFlowTimeoutService.clearFlowTimeout(yapFlowTypes.UPDATE_SETTINGS, isHolder);

    dispatch(setYapUpdateSettingsInProgress(true, isHolder));
    dispatch(setYapRequestActionTypeData({isHolder, actionType}));

    const assetId = makeSelectAssetIdByType(isHolder)(state);

    if (!assetId) {
        return updateSettingsFailedAction(isHolder, actionType);
    }

    await yapConnectionService.syncAndSubscribeOnDevice(isHolder);
    const isConnected = await cmService.waitUntilConnected({isHolder});

    if (!isConnected) {
        return updateSettingsFailedAction(isHolder, actionType);
    }

    if (isNeedCancel) {
        const isCancelSuccess = await waitUntilDataCollectionCanceled();

        if (!isCancelSuccess) {
            log.info(`DataCollection cancel is failed before ${actionType} process call. isHolder: ${isHolder}`);
            return updateSettingsFailedAction(isHolder, actionType);
        }
    }

    const response = isGamBackend
        ? await new GamClient().callPost({
              methodName,
              requestConfig: {args: assetId, params},
              isNeedRedirectForOtherErrors: false,
          })
        : await new UamClient().callPost({
              methodName,
              requestConfig: {args: assetId, params},
              isNeedRedirectForOtherErrors: false,
          });

    const {requestId} = response || {};
    const updateSettingsTimeout = isGamBackend
        ? gamTimeouts.GAM_UPDATE_SETTING_TIMEOUT
        : uamTimeouts.UAM_UPDATE_SETTING_TIMEOUT;

    const timeoutInSec = customTimeout ? customTimeout : updateSettingsTimeout;

    if (requestId) {
        let yapFlowType = yapFlowTypes.UPDATE_SETTINGS;

        dispatch(setYapLastRequestId(requestId, isHolder));
        if (actionType === yapRunCommandActionTypes.CLEAN_DEVICE) {
            yapFlowType = yapFlowTypes.AUTO_CLEAN;
            dispatch(setYapAutoCleaningInProgress(true));
            dispatch(setYapUpdateSettingsInProgress(false, isHolder));
        }

        yapFlowTimeoutService.createFlowTimeout({
            type: yapFlowType,
            timeoutInSec,
            isHolder,
        });

        return true;
    }

    return updateSettingsFailedAction(isHolder, actionType);
};

const getLogMessageForUpdateSettingsBody = (body) => {
    if (!body) return '';

    const {settingName, newValue} = body;

    if (!settingName) return '';

    return `SettingName: ${settingName}, new value: ${JSON.stringify(newValue)}`;
};

export default {
    runYapCommand,
    waitUntilDataCollectionCanceled,
    getLogMessageForUpdateSettingsBody,
};
